"use client";

import { useEffect, useRef, useState } from "react";

interface ScrambleRevealProps {
  /** The final text to reveal */
  text: string;
  className?: string;
  /** Characters cycled through before a character locks in */
  glyphs?: string;
  /** Total time for the whole reveal, in ms */
  duration?: number;
  /** Delay before the animation starts, in ms */
  startDelay?: number;
  /** Change this value to re-trigger the animation (e.g. on route change) */
  triggerKey?: string | number;
}

const DEFAULT_GLYPHS = "!<>-_\\/[]{}—=+*^?#01";

export default function ScrambleReveal({
  text,
  className,
  glyphs = DEFAULT_GLYPHS,
  duration = 1100,
  startDelay = 0,
  triggerKey,
}: ScrambleRevealProps) {
  const [display, setDisplay] = useState(text);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setDisplay(text);
      return;
    }

    const chars = text.split("");
    // Characters settle left-to-right: each gets a staggered start time
    // within the first 60% of the duration, then takes 40% to lock in.
    const charDuration = duration * 0.4;
    const charDelays = chars.map((_, i) =>
      chars.length > 1 ? (i / (chars.length - 1)) * (duration * 0.6) : 0
    );

    let startTime: number | null = null;
    let cancelled = false;

    const randomGlyph = () => glyphs[Math.floor(Math.random() * glyphs.length)];

    const tick = (now: number) => {
      if (cancelled) return;
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;

      const next = chars
        .map((char, i) => {
          if (char === " ") return " ";
          const localElapsed = elapsed - charDelays[i];
          if (localElapsed >= charDuration) return char;
          return randomGlyph();
        })
        .join("");

      setDisplay(next);

      const totalDuration = charDelays[charDelays.length - 1] + charDuration;
      if (elapsed < totalDuration) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(text);
      }
    };

    const timeout = setTimeout(() => {
      frameRef.current = requestAnimationFrame(tick);
    }, startDelay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [text, duration, startDelay, glyphs, triggerKey]);

  return (
    <span className={className}>
      <span aria-hidden="true">{display}</span>
      <span className="sr-only">{text}</span>
    </span>
  );
}